import type { ItemReturnRow, ItemTxnRow } from '@erp/shared';
import { FileText, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../../components/shadcn/index.js';
import { DataTable, type DataTableColumn, EmptyState } from '../../../components/ui/index.js';
import { api } from '../../../lib/api.js';
import { formatDate } from '../../../lib/intl.js';
import { formatMoney } from '../../../lib/money.js';

import { fmtQty } from './profileFormat.js';
import { LoadError, moneyList } from './ProfileParts.js';

/**
 * Where a document row links. `purchaseOrder` is empty ON PURPOSE: the purchase
 * order page is gone (a purchase is entered straight onto a bill), but the old
 * PO lines are still real history for this item, so they are listed — as text,
 * not as a link into a page that no longer exists.
 */
const DOC_PATH: Record<ItemTxnRow['docType'], string> = {
  invoice: '/sales/invoices',
  salesOrder: '/sales/orders',
  bill: '/purchasing/bills',
  purchaseOrder: '',
};

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'secondary'> = {
  paid: 'success',
  issued: 'info',
  approved: 'info',
  submitted: 'info',
  fulfilled: 'success',
  received: 'success',
  draft: 'secondary',
  cancelled: 'danger',
};

/** Sum a column per currency — amounts in two currencies are never one number. */
function perCurrency<T>(rows: T[], amount: (r: T) => string, currency: (r: T) => string): Array<{ currencyCode: string; amount: string }> {
  const m = new Map<string, number>();
  for (const r of rows) m.set(currency(r), (m.get(currency(r)) ?? 0) + Number(amount(r)));
  return [...m].map(([currencyCode, v]) => ({ currencyCode, amount: String(v) }));
}

/**
 * Sales or purchase document lines that carried this item, newest first:
 * where, to or from whom, how many (and how many base units that is), at what
 * price. Drafts and cancelled documents are listed with their status but kept
 * out of the totals — they moved nothing.
 */
export function ItemTxnTab({ itemId, kind, baseUnit }: { itemId: string; kind: 'sales' | 'purchases'; baseUnit: string }): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const locale = i18n.language;
  const [rows, setRows] = useState<ItemTxnRow[] | null>(null);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<'live' | 'all'>('live');

  useEffect(() => {
    if (!itemId) return undefined;
    let active = true;
    setRows(null); setError(false);
    void api().get<{ items: ItemTxnRow[] }>(`/items/${itemId}/${kind}`)
      .then((r) => { if (active) setRows(r.data.items); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [itemId, kind]);

  const live = (r: ItemTxnRow): boolean => r.status !== 'draft' && r.status !== 'cancelled';
  const shown = useMemo(() => (rows ?? []).filter((r) => status === 'all' || live(r)), [rows, status]);

  if (error) return <LoadError />;
  if (!rows) return <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>;
  if (rows.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState
          icon={<FileText className="h-7 w-7" aria-hidden />}
          title={kind === 'sales' ? t('detail.noSales', { defaultValue: 'No sales yet' }) : t('detail.noPurchases', { defaultValue: 'No purchases yet' })}
        />
      </Card>
    );
  }

  const baseQty = (r: ItemTxnRow): number => Number(r.quantity) * Number(r.unitFactor ?? '1');
  // A debit note is goods going BACK to the supplier — not a purchase to add up.
  const counted = shown.filter((r) => live(r) && !r.isReturn);
  const columns: DataTableColumn<ItemTxnRow>[] = [
    {
      id: 'date',
      header: t('detail.col.date', { defaultValue: 'Date' }),
      sortValue: (r) => r.docDate,
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (r) => formatDate(r.docDate, { locale }),
    },
    {
      id: 'document',
      header: t('detail.col.document', { defaultValue: 'Document' }),
      sortValue: (r) => r.docNumber,
      searchValue: (r) => r.docNumber,
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-1.5">
          {DOC_PATH[r.docType] ? (
            <Link to={`${DOC_PATH[r.docType]}/${r.docId}`} className="font-mono text-xs font-semibold text-brand hover:underline">{r.docNumber}</Link>
          ) : (
            <span className="font-mono text-xs font-semibold">{r.docNumber}</span>
          )}
          <span className="text-[11px] text-fg-subtle">{t(`detail.docType.${r.docType}`, { defaultValue: r.docType })}</span>
          {r.isPos && <Badge variant="info">{t('profile.docs.pos', { defaultValue: 'Register' })}</Badge>}
          {r.isReturn && <Badge variant="warning">{t('profile.docs.debitNote', { defaultValue: 'Returned to supplier' })}</Badge>}
        </span>
      ),
    },
    {
      id: 'party',
      header: kind === 'sales' ? t('profile.docs.customer', { defaultValue: 'Customer' }) : t('profile.docs.supplier', { defaultValue: 'Supplier' }),
      sortValue: (r) => r.partyName,
      searchValue: (r) => r.partyName,
      cell: (r) => r.partyName || '—',
    },
    {
      id: 'branch',
      header: t('profile.docs.branch', { defaultValue: 'Branch' }),
      sortValue: (r) => r.warehouseName ?? '',
      searchValue: (r) => r.warehouseName ?? '',
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (r) => r.warehouseName ?? '—',
    },
    {
      id: 'qty',
      header: t('detail.col.qty', { defaultValue: 'Qty' }),
      align: 'end',
      sortValue: (r) => baseQty(r),
      cell: (r) => (
        <span className="flex flex-col items-end leading-tight">
          <span className="font-mono tabular-nums">
            {fmtQty(r.quantity, locale)}{r.unitName ? <span className="ms-1 font-sans text-xs text-fg-muted">{r.unitName}</span> : null}
          </span>
          {Number(r.unitFactor ?? '1') !== 1 && (
            <span className="font-mono text-[11px] tabular-nums text-fg-muted">= {fmtQty(baseQty(r), locale)} {baseUnit}</span>
          )}
        </span>
      ),
    },
    {
      id: 'unitPrice',
      header: t('detail.col.unitPrice', { defaultValue: 'Unit price' }),
      align: 'end',
      sortValue: (r) => Number(r.unitPrice),
      cellClassName: 'whitespace-nowrap font-mono tabular-nums',
      cell: (r) => formatMoney(r.unitPrice, { locale, currency: r.currencyCode }),
    },
    {
      id: 'total',
      header: t('detail.col.total', { defaultValue: 'Total' }),
      align: 'end',
      sortValue: (r) => Number(r.lineTotal),
      cellClassName: 'whitespace-nowrap font-mono font-semibold tabular-nums',
      cell: (r) => formatMoney(r.lineTotal, { locale, currency: r.currencyCode }),
    },
    {
      id: 'status',
      header: t('profile.docs.status', { defaultValue: 'Status' }),
      sortValue: (r) => r.status,
      searchValue: (r) => t(`profile.status.${r.status}`, { defaultValue: r.status }),
      cell: (r) => <Badge variant={STATUS_TONE[r.status] ?? 'secondary'}>{t(`profile.status.${r.status}`, { defaultValue: r.status })}</Badge>,
    },
  ];

  const toolbar = (
    <select
      value={status}
      onChange={(e) => setStatus(e.target.value as 'live' | 'all')}
      className="h-9 rounded-full border border-border bg-bg px-3 text-xs font-semibold text-fg-muted outline-none focus:border-brand"
      aria-label={t('profile.docs.status', { defaultValue: 'Status' })}
    >
      <option value="live">{t('profile.docs.onlyLive', { defaultValue: 'Posted only' })}</option>
      <option value="all">{t('profile.docs.withDrafts', { defaultValue: 'Including drafts and cancelled' })}</option>
    </select>
  );

  return (
    <Card className="overflow-hidden p-0">
      <DataTable
        columns={columns}
        data={shown}
        rowKey={(r) => `${r.docType}-${r.docId}-${r.docNumber}-${r.quantity}-${r.unitPrice}`}
        initialSort={{ id: 'date', dir: 'desc' }}
        pageSize={25}
        toolbar={toolbar}
        rowClassName={(r) => (live(r) ? undefined : 'opacity-60')}
        footerRows={[{
          key: 'totals',
          className: 'font-semibold',
          cells: {
            date: t('profile.docs.totals', { defaultValue: 'Posted totals' }),
            qty: <span className="font-mono tabular-nums">{fmtQty(counted.reduce((s, r) => s + baseQty(r), 0), locale)} <span className="font-sans text-xs text-fg-muted">{baseUnit}</span></span>,
            total: <span className="font-mono tabular-nums">{moneyList(perCurrency(counted, (r) => r.lineTotal, (r) => r.currencyCode), locale)}</span>,
          },
        }]}
      />
    </Card>
  );
}

/** Credit notes that brought this item back, with the sale each one reverses. */
export function ItemReturnsTab({ itemId, baseUnit }: { itemId: string; baseUnit: string }): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const locale = i18n.language;
  const [rows, setRows] = useState<ItemReturnRow[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!itemId) return undefined;
    let active = true;
    setRows(null); setError(false);
    void api().get<{ items: ItemReturnRow[] }>(`/items/${itemId}/returns`)
      .then((r) => { if (active) setRows(r.data.items); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [itemId]);

  if (error) return <LoadError />;
  if (!rows) return <Card className="p-6"><Skeleton className="h-40 w-full" /></Card>;
  if (rows.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState icon={<Undo2 className="h-7 w-7" aria-hidden />} title={t('profile.returns.empty', { defaultValue: 'Nothing has been returned' })} />
      </Card>
    );
  }

  const baseQty = (r: ItemReturnRow): number => Number(r.quantity) * Number(r.unitFactor);
  const counted = rows.filter((r) => r.status !== 'cancelled');
  const columns: DataTableColumn<ItemReturnRow>[] = [
    {
      id: 'date',
      header: t('detail.col.date', { defaultValue: 'Date' }),
      sortValue: (r) => r.docDate,
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (r) => formatDate(r.docDate, { locale }),
    },
    {
      id: 'number',
      header: t('profile.returns.number', { defaultValue: 'Return #' }),
      sortValue: (r) => r.docNumber,
      searchValue: (r) => r.docNumber,
      cell: (r) => <Link to={`/sales/invoices/${r.creditNoteId}`} className="font-mono text-xs font-semibold text-brand hover:underline">{r.docNumber}</Link>,
    },
    {
      id: 'origin',
      header: t('profile.returns.invoice', { defaultValue: 'Original invoice' }),
      sortValue: (r) => r.parentDocNumber ?? '',
      searchValue: (r) => r.parentDocNumber ?? '',
      cell: (r) => (r.parentInvoiceId
        ? <Link to={`/sales/invoices/${r.parentInvoiceId}`} className="font-mono text-xs text-brand hover:underline">{r.parentDocNumber ?? '—'}</Link>
        : <span className="text-fg-subtle">—</span>),
    },
    {
      id: 'party',
      header: t('profile.docs.customer', { defaultValue: 'Customer' }),
      sortValue: (r) => r.partyName ?? '',
      searchValue: (r) => r.partyName ?? '',
      cell: (r) => r.partyName ?? '—',
    },
    {
      id: 'branch',
      header: t('profile.docs.branch', { defaultValue: 'Branch' }),
      sortValue: (r) => r.warehouseName ?? '',
      cellClassName: 'whitespace-nowrap text-fg-muted',
      cell: (r) => r.warehouseName ?? '—',
    },
    {
      id: 'qty',
      header: t('detail.col.qty', { defaultValue: 'Qty' }),
      align: 'end',
      sortValue: (r) => baseQty(r),
      cell: (r) => (
        <span className="flex flex-col items-end leading-tight">
          <span className="font-mono tabular-nums">{fmtQty(r.quantity, locale)}{r.unitName ? <span className="ms-1 font-sans text-xs text-fg-muted">{r.unitName}</span> : null}</span>
          {Number(r.unitFactor) !== 1 && <span className="font-mono text-[11px] tabular-nums text-fg-muted">= {fmtQty(baseQty(r), locale)} {baseUnit}</span>}
        </span>
      ),
    },
    {
      id: 'total',
      header: t('profile.returns.credited', { defaultValue: 'Credited' }),
      align: 'end',
      sortValue: (r) => Number(r.lineTotal),
      cellClassName: 'whitespace-nowrap font-mono font-semibold tabular-nums',
      cell: (r) => formatMoney(r.lineTotal, { locale, currency: r.currencyCode }),
    },
    {
      id: 'reason',
      header: t('profile.returns.reason', { defaultValue: 'Reason' }),
      searchValue: (r) => r.reason ?? '',
      cellClassName: 'max-w-[16rem] truncate text-fg-muted',
      cell: (r) => <span title={r.reason ?? undefined}>{r.reason ?? '—'}</span>,
    },
  ];

  return (
    <Card className="overflow-hidden p-0">
      <DataTable
        columns={columns}
        data={rows}
        rowKey={(r) => `${r.creditNoteId}-${r.quantity}-${r.unitPrice}`}
        initialSort={{ id: 'date', dir: 'desc' }}
        pageSize={25}
        rowClassName={(r) => (r.status === 'cancelled' ? 'opacity-60' : undefined)}
        footerRows={[{
          key: 'totals',
          className: 'font-semibold',
          cells: {
            date: t('profile.docs.totals', { defaultValue: 'Posted totals' }),
            qty: <span className="font-mono tabular-nums">{fmtQty(counted.reduce((s, r) => s + baseQty(r), 0), locale)} <span className="font-sans text-xs text-fg-muted">{baseUnit}</span></span>,
            total: <span className="font-mono tabular-nums">{moneyList(perCurrency(counted, (r) => r.lineTotal, (r) => r.currencyCode), locale)}</span>,
          },
        }]}
      />
    </Card>
  );
}
